/**
 * Building assistant (spec: workspace docs/specs/ai-assistant.md). Superadmin-only.
 *
 * Chats live in `aiChats/{chatKey}` and are written only by the mf-functions callables;
 * the web generates a new chat's key locally (no write), subscribes to it and calls
 * `askAssistant`, which creates the chat on its first question.
 */
import { getDatabase, ref, get, push } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { composeComponentDisplayName } from '../Helpers/componentDisplayName';
import { resolveBuildComponentTypeTranslation } from '../Helpers/BuildComponentTypesStore';
import { unitDisplayName } from '../Helpers/unitUtils';
import { leasesPath } from '../Helpers/leaseUtils';
import { translate } from '../types/EnumTranslations';
import { UnitKind } from '../types/Enums';
import { ASSISTANT_TURN_TIMEOUT_SECONDS } from '../types/assistant';
import type { AssistantRef } from '../utils/assistantReferences';
import { tenantPath } from '../types/tenants';

export const AI_CHATS_ROOT = 'aiChats';

interface AskRequest {
    chatKey: string;
    question: string;
    contextBuildingKey?: string;
}

interface FeedbackRequest {
    chatKey: string;
    turnKey: string;
    rating: 'up' | 'down';
    comment?: string;
}

/** What a reference in an answer shows and where it links; `href` absent = plain text. */
export interface ResolvedRef {
    label: string;
    href?: string;
}

const str = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value : undefined);
const unwrapValue = (value: unknown): unknown =>
    value && typeof value === 'object' && 'value' in (value as Record<string, unknown>)
        ? (value as Record<string, unknown>).value
        : value;

export class AssistantApi {
    private db = getDatabase();

    /** A push key for a new chat; nothing is written. */
    newChatKey(): string {
        const key = push(ref(this.db, AI_CHATS_ROOT)).key;
        if (!key) throw new Error('Kunde inte skapa en ny chatt');
        return key;
    }

    /** Runs one question. Progress and the answer arrive through the chat subscription. */
    async ask(request: AskRequest): Promise<void> {
        const callable = httpsCallable<AskRequest, { turnKey: string }>(functions, 'askAssistant', {
            // The function may run its full timeout; don't give up before it does.
            timeout: (ASSISTANT_TURN_TIMEOUT_SECONDS + 15) * 1000,
        });
        await callable(request);
    }

    async submitFeedback(request: FeedbackRequest): Promise<void> {
        const callable = httpsCallable<FeedbackRequest, { ok: true }>(functions, 'submitAssistantFeedback');
        await callable(request);
    }

    /**
     * Live label and link for a reference: one or two reads. Unknown → null.
     */
    async resolveRef(assistantRef: AssistantRef): Promise<ResolvedRef | null> {
        const [first, second] = assistantRef.keys;
        switch (assistantRef.kind) {
            case 'building': {
                const summary = await this.read(`buildingsSummary/${first}`);
                return summary ? { label: str(summary.name) ?? 'Byggnad', href: `/building/${first}` } : null;
            }
            case 'user': {
                // Display only; names are never stored with the chat or sent to the model.
                const user = await this.read(`users/userList/${first}`);
                return { label: str(user?.name) ?? 'Okänd användare' };
            }
            case 'component': {
                const c = await this.read(`buildings/${first}/structure/components/${second}`);
                if (!c) return null;
                const type = str(c.type) ?? '';
                const name = composeComponentDisplayName({
                    label: str(unwrapValue(c.label)),
                    isWindow: type === 'window',
                    sequentialNumber: typeof c.sequentialNumber === 'number' ? c.sequentialNumber : undefined,
                    roofShapeName: str(unwrapValue(c.roofShape)) ? translate(String(unwrapValue(c.roofShape))) : undefined,
                    specificTypeName: resolveBuildComponentTypeTranslation(str(unwrapValue(c.specificType))),
                    baseTypeName: type ? translate(type) : 'Komponent',
                    cardinalDirectionName: str(unwrapValue(c.cardinalDirection))
                        ? translate(String(unwrapValue(c.cardinalDirection)))
                        : undefined,
                });
                return { label: name, href: `/building/${first}/component/${second}` };
            }
            case 'workOrder': {
                const wo = (await this.read(`workOrders/active/${first}`)) ?? (await this.read(`workOrders/archive/${first}`));
                return wo ? { label: str(wo.title) ?? 'Ärende', href: `/management?wo=${first}` } : null;
            }
            case 'issue': {
                const active = await this.read(`issues/active/${first}`);
                if (active) return { label: str(active.title) ?? 'Felanmälan', href: `/management?issue=${first}` };
                const archived = await this.read(`issues/archive/${first}`);
                return archived ? { label: str(archived.title) ?? 'Felanmälan' } : null;
            }
            case 'project': {
                const p = await this.read(`projects/${first}`);
                if (!p) return null;
                const buildingKey = str(p.buildingKey);
                return {
                    label: str(p.title) ?? 'Projekt',
                    href: buildingKey ? `/building/${buildingKey}/projects/${first}` : undefined,
                };
            }
            case 'lease': {
                const lease = await this.read(`leases/${first}`);
                if (!lease) return null;
                const object = lease.object as Record<string, unknown> | undefined;
                const buildingKey = str(lease.buildingKey);
                return {
                    label: str(object?.label) ?? 'Hyresavtal',
                    href: buildingKey ? leasesPath(buildingKey, first) : undefined,
                };
            }
            case 'tenant': {
                const tenant = await this.read(`tenants/${first}`);
                return tenant ? { label: str(tenant.name) ?? 'Hyresgäst', href: tenantPath(first) } : null;
            }
            case 'unit': {
                const unit = await this.read(`buildings/${first}/units/${second}`);
                if (!unit) return null;
                const kind = unit.kind === UnitKind.Commercial ? UnitKind.Commercial : UnitKind.Apartment;
                return { label: unitDisplayName({ kind, number: str(unit.number) ?? '' }), href: `/building/${first}/units?unit=${second}` };
            }
            default:
                return null;
        }
    }

    private async read(path: string): Promise<Record<string, unknown> | null> {
        const snapshot = await get(ref(this.db, path));
        const value = snapshot.val();
        return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
    }
}

export const assistantApi = new AssistantApi();
